import { useParams, Link, useNavigate } from 'react-router-dom';
import { useProductBySlug, useAllProducts, useCategories } from '@/hooks/useProducts';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useCart } from '@/hooks/useCart';
import { Star, Check, Truck, Shield, Tag, ChevronDown, TrendingUp, Zap, Sparkles, Loader2, ShoppingCart, Lock, Flame, Package } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import ReviewSection from '@/components/ReviewSection';
import ProductDescription from '@/components/ProductDescription';
import EmojiOrFlag from '@/components/EmojiOrFlag';
import OriginSelector from '@/components/OriginSelector';
import SEOHead, { productSchema, breadcrumbSchema, faqSchema } from '@/components/SEOHead';
import { useLanguage, ROUTE_SEGMENTS, type Locale, NON_DEFAULT_LOCALES, DEFAULT_LOCALE } from '@/hooks/useLanguage';
import { supabase } from '@/integrations/supabase/client';
import PaymentIcons from '@/components/PaymentIcons';
import PageTransition from '@/components/PageTransition';
import { useProductTranslation, applyProductTranslation, useCategoryTranslation, applyCategoryTranslation, useProductTypeTranslations, useProductVariationTranslations, useProductGiftTranslations, useProductTranslations } from '@/hooks/useContentTranslation';
import { useTranslations } from '@/hooks/useTranslations';
import { useOmnisendTracking } from '@/hooks/useOmnisendTracking';
import { useAuth } from '@/hooks/useAuth';
import { ClipboardPaste } from 'lucide-react';
import { getLastOrder } from '@/lib/lastOrder';
import { getOrderCount } from '@/lib/orderCount';
import { useProductOrderCounts } from '@/hooks/useProductOrderCounts';
import PointsEarnInfo from '@/components/loyalty/PointsEarnInfo';
import { transformImage } from '@/lib/imageTransform';
import { formatPrice } from '@/lib/formatPrice';
import { DisplayPrice } from '@/components/DisplayPrice';
import { useStorefront, ROOT_PRODUCT_STOREFRONTS } from '@/hooks/useStorefront';
import { useExchangeRate } from '@/hooks/useExchangeRate';
import { maybeAlImageUrl } from '@/lib/alImageUrl';
import { convertPrice } from '@/lib/priceConversion';
import { roundPrice } from '@/lib/priceRounding';
import { sanitizeRichForStorefront } from '@/lib/productDescriptionAT';

/**
 * Storefront-aware Intl locale for number formatting (thousands separator).
 * CH → 1'385, AT → 1.385 (de-AT), IT → 1.385 (it-IT).
 */
function numberLocaleFor(sf: string): string {
  if (sf === 'it') return 'it-IT';
  if (sf === 'at') return 'de-AT';
  return 'de-CH';
}

/**
 * Deterministisches Zahl-Formatting mit Tausender-Trennzeichen — funktioniert
 * auch in Prerender-Environments ohne vollständiges ICU (wo `toLocaleString`
 * die Gruppierung stillschweigend weglässt). Damit "1.385" auf IT garantiert
 * auch im ersten HTML-Snapshot steht.
 */
function formatIntFor(sf: string, n: number): string {
  const sep = sf === 'ch' ? '\u2019' /* ’ */ : '.'; // AT + IT nutzen Punkt.
  const s = Math.round(n).toString();
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

export default function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { locale, localePath } = useLanguage();
  const { product: rawProduct, loading } = useProductBySlug(slug, locale);
  const { categories } = useCategories();
  const { products: allProducts } = useAllProducts();
  const { addItem } = useCart();
  const { user } = useAuth();
  const { t } = useTranslations();
  const { trackProductView, trackAddToCart } = useOmnisendTracking();
  const sf = useStorefront();
  // JSON-LD offers must quote the storefront currency, not the CHF basis.
  // CH keeps the fastpath (no rate lookup, unchanged output).
  const { getRate: getSchemaRate } = useExchangeRate();

  const { overlay: productOverlay, loading: productTranslationLoading } = useProductTranslation(rawProduct?.id);
  const product = useMemo(() => applyProductTranslation(rawProduct, productOverlay), [rawProduct, productOverlay]);
  const orderCounts = useProductOrderCounts(rawProduct?.id);

  // Live-Tick für die "Zuletzt bestellt vor X Min." Anzeige: alle 60s neu rendern.
  // Rein clientseitig (setInterval) — keine Cloud-Calls.
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!slug || !rawProduct?.slug || locale === DEFAULT_LOCALE) return;
    // Root-product storefronts (AL) serve products at /<slug> — never bounce
    // them into the /produkt/ prefix. CH/AT/IT behaviour unchanged.
    if ((ROOT_PRODUCT_STOREFRONTS as readonly string[]).includes(sf.storefront)) return;
    // Only redirect if the URL contains the German slug and we have a translated slug
    if (slug === rawProduct.slug && productOverlay?.slug?.trim()) {
      navigate(localePath(`/produkt/${productOverlay.slug}`), { replace: true });
    }
  }, [slug, rawProduct?.slug, productOverlay?.slug, locale, localePath, navigate, sf.storefront]);

  const rawCategory = categories.find((c: any) => c.id === product?.category_id);
  const categoryOverlay = useCategoryTranslation(rawCategory?.id);
  const category = useMemo(() => applyCategoryTranslation(rawCategory, categoryOverlay), [rawCategory, categoryOverlay]);

  // Fetch translations for types, variations, and gifts
  const typeIds = useMemo(() => (product?.product_types || []).map((t: any) => t.id), [product?.product_types]);
  const variationIds = useMemo(() => (product?.product_variations || []).map((v: any) => v.id), [product?.product_variations]);
  const giftIds = useMemo(() => ((product as any)?.product_gifts || []).map((g: any) => g.id), [(product as any)?.product_gifts]);

  const { overlays: typeTranslations, loading: typeTranslationsLoading } = useProductTypeTranslations(typeIds);
  const { overlays: variationTranslations, badgeOverlays: variationBadgeTranslations, loading: variationTranslationsLoading } = useProductVariationTranslations(variationIds);
  const badgeLabelFor = (v: any) => variationBadgeTranslations[v?.id] || v?.badge_label;
  const { overlays: giftTranslations, loading: giftTranslationsLoading } = useProductGiftTranslations(giftIds);

  const translationsLoading =
    productTranslationLoading || typeTranslationsLoading || variationTranslationsLoading || giftTranslationsLoading;

  const localizedProductSlug = productOverlay?.slug?.trim();
  const slugReady = !!slug && !!rawProduct && (
    locale === DEFAULT_LOCALE
      ? slug === rawProduct.slug
      : slug === rawProduct.slug || (!!localizedProductSlug && slug === localizedProductSlug)
  );

  // Fetch all locale slugs for hreflang
  const [allLocaleSlugs, setAllLocaleSlugs] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!rawProduct?.id) return;
    supabase
      .from('product_translations')
      .select('locale, slug')
      .eq('product_id', rawProduct.id)
      .then(({ data }) => {
        const map: Record<string, string> = {};
        for (const row of data || []) {
          if (row.slug?.trim()) map[row.locale] = row.slug;
        }
        setAllLocaleSlugs(map);
      });
  }, [rawProduct?.id]);

  const [selectedVariationId, setSelectedVariationId] = useState<string | null>(null);
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [selectedGiftId, setSelectedGiftId] = useState<string | null>(null);
  const [target, setTarget] = useState('');
  const [giftTarget, setGiftTarget] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [missingFields, setMissingFields] = useState<Set<'target' | 'giftTarget' | 'confirm'>>(new Set());
  const clearMissing = (field: 'target' | 'giftTarget' | 'confirm') => {
    setMissingFields(prev => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };
  const targetFieldRef = useRef<HTMLDivElement | null>(null);
  const giftTargetFieldRef = useRef<HTMLDivElement | null>(null);
  const confirmFieldRef = useRef<HTMLLabelElement | null>(null);
  const [openDropdown, setOpenDropdown] = useState<'type' | 'variation' | 'origin' | 'gift' | null>(null);
  const [schemaReviews, setSchemaReviews] = useState<{ author: string; rating: number; comment: string; date: string; verifiedPurchase?: boolean }[]>([]);
  const [aiDescription, setAiDescription] = useState('');
  const [aiLanguage, setAiLanguage] = useState('Deutsch');
  const [aiEmojis, setAiEmojis] = useState<'yes' | 'no' | 'few'>('few');
  const [aiGenerating, setAiGenerating] = useState(false);
  const customInputTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const customInputGutterRef = useRef<HTMLDivElement | null>(null);
  

  // Close dropdown on outside click
  useEffect(() => {
    if (!openDropdown) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [openDropdown]);

  // Reset all form state when product changes
  useEffect(() => {
    setSelectedVariationId(null);
    setSelectedTypeId(null);
    setSelectedGiftId(null);
    setTarget('');
    setGiftTarget('');
    setCustomInput('');
    setConfirmed(false);
    setOpenDropdown(null);
    setMissingFields(new Set());
  }, [rawProduct?.id]);

  // Track product view for Omnisend
  useEffect(() => {
    if (!product?.id || !slugReady) return;
    const firstVariation = product.product_variations?.[0];
    trackProductView({
      id: product.id,
      title: product.title,
      slug: product.slug,
      price: firstVariation?.price || 0,
      imageUrl: product.image_url || undefined,
      categoryId: product.category_id || undefined,
    }, user?.email || undefined);
  }, [product?.id, slugReady, user?.email]);

  // Fetch approved reviews for schema markup
  useEffect(() => {
    if (!product?.id) return;
    supabase
      .from('reviews')
      .select('author_name, rating, comment, created_at, verified_purchase')
      .eq('product_id', product.id)
      .eq('is_approved', true)
      .order('created_at', { ascending: false })
      .limit(10)
      .then(({ data }) => {
        if (data) {
          setSchemaReviews(data.map((r: any) => ({
            author: r.author_name,
            rating: r.rating,
            comment: r.comment || '',
            date: r.created_at.split('T')[0],
            verifiedPurchase: !!r.verified_purchase,
          })));
        }
      });
  }, [product?.id]);

  // Sort product types
  const sortedTypes = useMemo(() => {
    if (!product?.product_types || product.product_types.length === 0) return [];
    return [...product.product_types].sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [product?.product_types]);

  const hasTypes = sortedTypes.length > 0;

  // Auto-select first type when product loads
  if (hasTypes && !selectedTypeId) {
    const recommended = sortedTypes.find((t: any) => t.is_recommended);
    setSelectedTypeId((recommended ?? sortedTypes[0]).id);
  }

  // Filter variations based on selected type
  const sortedVariations = useMemo(() => {
    if (!product) return [];
    let vars = [...product.product_variations];
    if (hasTypes && selectedTypeId) {
      vars = vars.filter((v: any) => v.product_type_id === selectedTypeId);
    } else if (hasTypes && !selectedTypeId) {
      return [];
    }
    return vars.sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [product, hasTypes, selectedTypeId]);

  // Auto-select first variation when variations change
  useEffect(() => {
    if (sortedVariations.length > 0 && !sortedVariations.find((v: any) => v.id === selectedVariationId)) {
      const defaultVar = sortedVariations.find((v: any) => v.is_default_selected);
      setSelectedVariationId((defaultVar ?? sortedVariations[0]).id);
    }
  }, [sortedVariations, selectedVariationId]);

  // Sort gifts
  const sortedGifts = useMemo(() => {
    if (!product?.product_gifts || (product as any).product_gifts.length === 0) return [];
    return [...(product as any).product_gifts].sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [(product as any)?.product_gifts]);

  // Default gift to null (no gift / "Nein, danke")
  useEffect(() => {
    if (sortedGifts.length > 0 && selectedGiftId === undefined) {
      setSelectedGiftId(null);
    }
  }, [sortedGifts]);

  const selectedGift = sortedGifts.find((g: any) => g.id === selectedGiftId);

  // Related products with translations (hooks must be before early returns)
  const rawRelatedProducts = useMemo(() => 
    allProducts
      .filter(p => p.category_id === (product ?? rawProduct)?.category_id && p.id !== (product ?? rawProduct)?.id)
      .slice(0, 4),
    [allProducts, product, rawProduct]
  );
  const relatedProductIds = useMemo(() => rawRelatedProducts.map(p => p.id), [rawRelatedProducts]);
  const relatedOverlays = useProductTranslations(relatedProductIds);

  if (loading || translationsLoading || !slugReady) {
    return <div className="min-h-screen" />;
  }

  if (!product) {
    return (
      <div className="container py-24 text-center">
        <h1 className="mb-4 text-2xl font-bold">{t('general.error', 'Produkt nicht gefunden')}</h1>
        <Link to={localePath('/')} className="text-primary hover:underline">{t('general.back', 'Zurück zum Shop')}</Link>
      </div>
    );
  }

  const variation = sortedVariations.find((v: any) => v.id === selectedVariationId);
  const relatedProducts = rawRelatedProducts.map(p => applyProductTranslation(p, relatedOverlays[p.id] || null));

  // Get selected type for custom input
  const selectedType = hasTypes ? sortedTypes.find((t: any) => t.id === selectedTypeId) : null;
  const customInputLabel = selectedType?.custom_input_label?.trim() || '';
  const customInputPlaceholder = selectedType?.custom_input_placeholder?.trim() || '';
  const aiGenerateEnabled = !!(selectedType as any)?.ai_generate_enabled;

  const handleTypeChange = (typeId: string) => {
    setSelectedTypeId(typeId);
    setSelectedVariationId(null);
    setCustomInput('');
  };

  const handleAddToCart = () => {
    if (!variation) return;
    const giftNeedsTarget = selectedGift?.input_label;

    // Collect ALL missing fields, mark them, scroll to the first
    const missing: Array<'target' | 'giftTarget' | 'confirm'> = [];
    if (!target.trim()) missing.push('target');
    if (selectedGift && giftNeedsTarget && !giftTarget.trim()) missing.push('giftTarget');
    if (!confirmed) missing.push('confirm');

    if (missing.length > 0) {
      setMissingFields(new Set(missing));
      const first = missing[0];
      const ref =
        first === 'target'
          ? targetFieldRef
          : first === 'giftTarget'
            ? giftTargetFieldRef
            : confirmFieldRef;
      const msg =
        first === 'target'
          ? t('product.error_target_required', 'Bitte fülle das Feld "{label}" aus.').replace('{label}', product.input_label || '')
          : first === 'giftTarget'
            ? t('product.error_gift_target_required', 'Bitte fülle das Geschenk-Feld aus.')
            : t('product.error_confirm_required', 'Bitte bestätige, dass dein Profil öffentlich ist.');
      toast.error(msg);
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    addItem({
      productId: product.id,
      variationId: variation.id,
      target,
      quantity: variation.quantity,
      price: variation.price,
      productTitle: product.title,
      productImage: product.image_url || undefined,
      variationLabel: variationTranslations[variation.id] || variation.label,
      ...(selectedType ? { typeLabel: typeTranslations[selectedType.id]?.label || selectedType.label } : {}),
      ...(customInput ? { customInput } : {}),
      ...(selectedGift ? {
        giftId: selectedGift.id,
        giftLabel: giftTranslations[selectedGift.id]?.label?.trim() || selectedGift.label,
        giftTarget: giftNeedsTarget ? giftTarget : target,
        giftQuantity: selectedGift.quantity,
        giftSmmPanelId: selectedGift.smm_panel_id || undefined,
        giftSmmServiceId: selectedGift.smm_service_id || undefined,
      } : {}),
    });
    // Track add to cart for Omnisend
    const newItem = {
      productId: product.id,
      variationId: variation.id,
      target,
      quantity: variation.quantity,
      price: variation.price,
      productTitle: product.title,
      productImage: product.image_url || undefined,
      variationLabel: variationTranslations[variation.id] || variation.label,
    };
    const allItems = [...(JSON.parse(localStorage.getItem('gaingrid-cart') || '[]'))];
    const cartTotal = allItems.reduce((s: number, i: any) => s + (i.price || 0), 0);
    trackAddToCart(newItem as any, allItems, cartTotal, user?.email || undefined);
    setTarget('');
    setGiftTarget('');
    setCustomInput('');
    setConfirmed(false);
  };

  const giftNeedsTarget = selectedGift?.input_label;
  const canAdd = !!variation && !!target && confirmed && (!selectedGift || !giftNeedsTarget || !!giftTarget);

  // Price range across all variations
  const allVars = product.product_variations;
  const priceRange = allVars.length > 0
    ? { low: Math.min(...allVars.map((v: any) => v.price)), high: Math.max(...allVars.map((v: any) => v.price)) }
    : undefined;

  // Storefront origin for structured data. Only AL deviates today — CH/AT/IT
  // keep the historical CH domain, so their output stays byte-identical.
  const schemaOrigin = sf.storefront === 'al' ? 'https://bigfollow.al' : 'https://bigfollow.ch';
  const productSchemaPath = sf.storefront === 'al'
    ? localePath(`/produkt/${product.slug}`)
    : `/produkt/${product.slug}`;

  const breadcrumbItems = [
    { name: 'Home', url: `${schemaOrigin}/` },
    ...(category ? [{ name: category.name, url: `${schemaOrigin}/${category.slug}` }] : []),
    { name: product.title, url: `${schemaOrigin}${productSchemaPath}` },
  ];

  // Offer amounts in the storefront currency. Scoped to AL for now so
  // CH/AT/IT structured data stays byte-identical.
  const schemaIsAl = sf.storefront === 'al';
  const schemaRate = schemaIsAl ? getSchemaRate(sf.currency) : null;
  const schemaPriceRange = priceRange && schemaIsAl && schemaRate != null
    ? {
        low: roundPrice(convertPrice(priceRange.low, schemaRate), sf.currency),
        high: roundPrice(convertPrice(priceRange.high, schemaRate), sf.currency),
      }
    : priceRange;

  // Extract FAQ from rich_description for JSON-LD
  const richDesc = sanitizeRichForStorefront(product.rich_description, sf.storefront) as any;
  const faqItems = richDesc?.faq?.map((f: any) => ({ question: f.question, answer: f.answer })) || [];

  const seoTitle = product.meta_title || `${product.title} kaufen – ab ${formatPrice(priceRange?.low ?? 0)}`;
  const seoDescription = product.meta_description || product.short_description || product.description?.slice(0, 155) || `${product.title} günstig kaufen bei BigFollow. Sofortige Lieferung.`;

  const jsonLdSchemas: Record<string, unknown>[] = [
    productSchema({
      name: product.title,
      description: product.short_description || product.description || '',
      image: maybeAlImageUrl(product.image_url) || undefined,
      slug: product.slug,
      rating: product.rating ?? undefined,
      reviewCount: product.review_count ?? undefined,
      priceRange: schemaPriceRange,
      reviews: schemaReviews,
      currency: schemaIsAl ? sf.currency : undefined,
      siteUrl: schemaOrigin,
      path: productSchemaPath,
    }),
    breadcrumbSchema(breadcrumbItems),
  ];

  if (faqItems.length > 0) {
    jsonLdSchemas.push(faqSchema(faqItems));
  }

  return (
    <PageTransition ready={!loading && !!product && !translationsLoading && slugReady}>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
          canonical={`https://bigfollow.ch${localePath(`/produkt/${product.slug}`)}`}
        ogType="product"
        ogImage={maybeAlImageUrl(product.image_url) || undefined}
        jsonLd={jsonLdSchemas}
        overrideScope={{ scope: 'product', scope_key: product.id }}
        hreflangPaths={[
          { locale: 'de' as Locale, path: `/produkt/${product.slug}` },
          ...NON_DEFAULT_LOCALES.map(loc => ({
            locale: loc,
            path: `/${loc}/${ROUTE_SEGMENTS.produkt[loc]}/${allLocaleSlugs[loc] || product.slug}`,
          })),
        ]}
      />
      {/* Breadcrumb */}
      <div className="border-b bg-card">
        <div className="container py-3 text-sm text-muted-foreground">
          <Link to={localePath('/')} className="hover:text-foreground">{t('nav.breadcrumb_home', 'Home')}</Link>
          <span className="mx-2">»</span>
          {category && (
            <>
              <Link to={localePath(`/${category.slug}`)} className="hover:text-foreground">{category.name}</Link>
              <span className="mx-2">»</span>
            </>
          )}
          <span className="text-foreground">{product.title}</span>
        </div>
      </div>

      <div className="container py-8">
        <div className="grid gap-10 lg:grid-cols-2">
          {/* Left: Configurator */}
          <div>
            <div className="mb-6 flex items-center gap-4">
              {product.image_url && (
                <img
                  src={transformImage(product.image_url, { width: 64, height: 64, resize: 'contain' })}
                  alt={product.image_alt || `${product.title} kaufen bei BigFollow`}
                  title={product.image_title || undefined}
                  className="h-16 w-16 object-contain"
                  width={64}
                  height={64}
                />
              )}
              <div>
                <h1 className="text-2xl font-bold md:text-3xl">{product.h1_title || product.title}</h1>
                <div className="mt-1 flex items-center gap-1.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${i < Math.round(product.rating ?? 0) ? 'fill-warning text-warning' : 'text-border'}`}
                    />
                  ))}
                  <button
                    onClick={() => document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' })}
                    className="ml-1 text-sm text-muted-foreground hover:text-primary hover:underline transition-colors"
                  >
                    ({product.review_count ?? 0} {t('product.reviews', 'Bewertungen')})
                  </button>
                </div>
                <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-600">
                  <TrendingUp className="h-3 w-3" />
                  {(() => {
                    const base = getOrderCount(product.id, product.slug);
                    return `${formatIntFor(sf.storefront, base)}+ ${t('product.badge_orders', 'Bestellungen')}`;
                  })()}
                </span>
                {(() => {
                  // d7 statt d30 → Tier + Cap-Wahl passt zur echten Frequenz.
                  // getLastOrder gibt bei d7=0 null zurück (Nische → Zeile ausblenden).
                  const last = getLastOrder(product.id, product.slug, locale, orderCounts.d7, nowTick, sf.storefront);
                  if (!last) return null;
                  return (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                      </span>
                      <span>
                        {t('product.last_ordered', 'Zuletzt bestellt')} {last.agoLabel} {t('product.from_city', 'aus')} <span className="font-medium text-foreground">{last.city}</span>
                      </span>
                    </p>
                  );
                })()}
              </div>
            </div>

            {/* Info banner */}
            <div className="mb-6 rounded-lg bg-primary py-2 text-center text-sm font-medium text-primary-foreground">
              <span className="font-semibold">{t('product.important', 'Wichtig')}:</span> {t('product.profile_public', 'Profil muss öffentlich sein.')}
            </div>

            {/* Origin selector */}
            {slug && <OriginSelector currentSlug={slug} isOpen={openDropdown === 'origin'} onToggle={() => setOpenDropdown(openDropdown === 'origin' ? null : 'origin')} />}

            {/* Type selector */}
            {hasTypes && (() => {
              const selectedType = sortedTypes.find((t: any) => t.id === selectedTypeId);
              const recommendedType = sortedTypes.find((t: any) => t.is_recommended);
              const warningMsg = (selectedType as any)?.warning_message?.trim();
              return (
                <div className="mb-4 flex items-center gap-4 border-b pb-4">
                  <label className="w-36 shrink-0 text-sm font-semibold">
                    {product.type_selector_label || t('product.select_type', 'Typ auswählen')}
                  </label>
                  <div className="relative flex-1" data-dropdown>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === 'type' ? null : 'type')}
                      className="flex w-full items-center justify-between rounded-lg border bg-card px-4 py-2.5 text-sm transition-all hover:border-primary/40"
                    >
                      <span className="flex items-center gap-1.5">{selectedType ? <><span>{typeTranslations[selectedType.id]?.label || selectedType.label}</span>{selectedType.emoji && <EmojiOrFlag emoji={selectedType.emoji} />}{(selectedType as any).is_recommended && <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">{t('product.recommended', 'Empfohlen')}</span>}</> : t('product.please_select', 'Bitte auswählen')}</span>
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${openDropdown === 'type' ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === 'type' && (
                      <div className="absolute z-20 mt-1 w-full rounded-lg border bg-card shadow-lg">
                        {sortedTypes.map((tp: any) => {
                          const isActive = tp.id === selectedTypeId;
                          return (
                            <button
                              key={tp.id}
                              onClick={() => {
                                setOpenDropdown(null);
                                handleTypeChange(tp.id);
                              }}
                              className={`flex w-full items-center px-4 py-2.5 text-left text-sm transition-all first:rounded-t-lg last:rounded-b-lg ${
                                isActive
                                  ? 'bg-primary font-semibold text-primary-foreground'
                                  : 'text-foreground hover:bg-accent'
                              }`}
                            >
                              <span className="flex items-center gap-1.5">{typeTranslations[tp.id]?.label || tp.label}{tp.emoji && <EmojiOrFlag emoji={tp.emoji} />}{tp.is_recommended && <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isActive ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`}>{t('product.recommended', 'Empfohlen')}</span>}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {warningMsg && (
                      <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        <span className="mt-0.5">⚠️</span>
                        <div className="flex-1">
                          <p>{warningMsg}</p>
                          {recommendedType && recommendedType.id !== selectedType?.id && (
                            <button
                              onClick={() => handleTypeChange(recommendedType.id)}
                              className="mt-1 font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950"
                            >
                              {t('product.switch_to_type', 'Zu {name} wechseln →').replace('{name}', typeTranslations[recommendedType.id]?.label || recommendedType.label)}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Variation selector */}
            {(sortedVariations.length > 0 || !hasTypes) && (
              <div className="mb-4 flex items-center gap-4 border-b pb-4">
                <label className="w-36 shrink-0 text-sm font-semibold">
                  {(product as any).variation_selector_label?.trim() || t('product.quantity', 'Anzahl')}
                </label>
                <div className="flex-1">
                {sortedVariations.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {hasTypes ? t('product.select_type_first', 'Bitte wähle zuerst einen Typ aus.') : t('product.no_variations', 'Keine Variationen verfügbar.')}
                  </p>
                ) : (() => {
                  const basePricePerUnit = sortedVariations[0].price / sortedVariations[0].quantity;
                  const selectedVar = sortedVariations.find((v: any) => v.id === selectedVariationId);

                  /** Show label text in label mode, otherwise show quantity */
                  const isLabelMode = product?.label_mode === 'label';
                  const shortLabel = (v: any) => {
                    if (isLabelMode) {
                      return variationTranslations[v.id] || v.label;
                    }
                    if (typeof v.quantity === 'number' && Number.isFinite(v.quantity)) {
                     return formatIntFor(sf.storefront, v.quantity);
                    }
                    return variationTranslations[v.id] || v.label;
                  };

                  return (
                    <div className="relative" data-dropdown>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === 'variation' ? null : 'variation')}
                        className="flex w-full items-center justify-between gap-2 rounded-lg border bg-card px-4 py-2.5 text-sm transition-all hover:border-primary/40"
                      >
                        <span className="min-w-0 flex-1">
                          {selectedVar ? (
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className="font-medium">{shortLabel(selectedVar)}</span>
                              <span className="font-mono font-bold text-primary"><DisplayPrice chf={selectedVar.price} variationId={selectedVar.id} /></span>
                              {(() => {
                                const ppu = selectedVar.price / selectedVar.quantity;
                                const disc = Math.round((1 - ppu / basePricePerUnit) * 100);
                                return disc > 0 ? (
                                  <span className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">-{disc}%</span>
                                ) : null;
                              })()}
                              {badgeLabelFor(selectedVar) && (
                                <span
                                  title={badgeLabelFor(selectedVar)}
                                  className="hidden sm:inline-flex items-center gap-1 rounded-full bg-destructive px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-destructive-foreground"
                                >
                                  <Flame className="h-3 w-3" />
                                  <span>{badgeLabelFor(selectedVar)}</span>
                                </span>
                              )}
                            </span>
                          ) : t('product.please_select', 'Bitte auswählen')}
                        </span>
                        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${openDropdown === 'variation' ? 'rotate-180' : ''}`} />
                      </button>
                      {openDropdown === 'variation' && (
                        <div className="absolute z-20 mt-1 w-full rounded-lg border bg-card shadow-lg">
                          {sortedVariations.map((v: any, index: number) => {
                            const pricePerUnit = v.price / v.quantity;
                            const discountPercent = index === 0
                              ? 0
                              : Math.round((1 - pricePerUnit / basePricePerUnit) * 100);
                            const isActive = v.id === selectedVariationId;

                            return (
                              <button
                                key={v.id}
                                onClick={() => {
                                  setSelectedVariationId(v.id);
                                  setOpenDropdown(null);
                                }}
                                className={`flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-all first:rounded-t-lg last:rounded-b-lg ${
                                  isActive
                                    ? 'bg-primary font-semibold text-primary-foreground'
                                    : 'text-foreground hover:bg-accent'
                                }`}
                              >
                                <span className="flex min-w-0 flex-1 items-center gap-2 font-medium">
                                  {shortLabel(v)}
                                  {badgeLabelFor(v) && (
                                    <span
                                      title={badgeLabelFor(v)}
                                      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide sm:px-2 ${
                                        isActive
                                          ? 'bg-primary-foreground/20 text-primary-foreground'
                                          : 'bg-destructive text-destructive-foreground'
                                      }`}
                                    >
                                      <Flame className="h-3 w-3" />
                                      <span className="hidden sm:inline">{badgeLabelFor(v)}</span>
                                    </span>
                                  )}
                                </span>
                                <span className="flex shrink-0 items-center gap-2">
                                  <span className={`font-mono font-bold ${isActive ? 'text-primary-foreground' : 'text-primary'}`}>
                                    <DisplayPrice chf={v.price} variationId={v.id} />
                                  </span>
                                  {discountPercent > 0 && (
                                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                                      isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-primary text-primary-foreground'
                                    }`}>
                                      -{discountPercent}%
                                    </span>
                                  )}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
                </div>
              </div>
            )}

            {/* Target input */}
            <div
              ref={targetFieldRef}
              className={`mb-4 flex items-center gap-4 border-b pb-4 group/target rounded-lg transition-all ${
                missingFields.has('target') ? 'ring-2 ring-destructive ring-offset-2 bg-destructive/5 px-2 py-2' : ''
              }`}
            >
              <label className="w-36 shrink-0 text-sm font-semibold">{product.input_label}</label>
              <div className="relative w-full flex-1">
                <input
                  type="text"
                  value={target}
                  onChange={e => { setTarget(e.target.value); if (e.target.value.trim()) clearMissing('target'); }}
                  placeholder={product.input_placeholder || ''}
                  className="w-full rounded-lg border bg-card px-4 py-2.5 pr-9 text-sm outline-none transition-all focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                {!target && navigator.clipboard?.readText && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const text = await navigator.clipboard.readText();
                        if (text) setTarget(text.trim());
                      } catch { /* clipboard permission denied */ }
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground/50 transition-colors hover:text-primary hover:bg-primary/5"
                    title={t('product.paste', 'Einfügen')}
                  >
                    <ClipboardPaste className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Custom input (e.g. custom comments) – numbered lines, limited to selected quantity */}
            {customInputLabel && variation && (() => {
              const maxLines = variation.quantity;
              const normalizedLines = customInput ? customInput.split('\n').slice(0, maxLines) : [];
              const visibleLineCount = Math.max(normalizedLines.length + 1, Math.min(maxLines, 10));
              const filledCount = normalizedLines.filter(l => l.trim()).length;

              const setNormalizedInput = (value: string) => {
                const nextLines = value.replace(/\r\n/g, '\n').split('\n').slice(0, maxLines);
                setCustomInput(nextLines.join('\n'));
              };

              const syncCustomInputScroll = () => {
                if (!customInputTextareaRef.current || !customInputGutterRef.current) return;
                customInputGutterRef.current.scrollTop = customInputTextareaRef.current.scrollTop;
              };

              const handleAiGenerate = async () => {
                if (aiGenerating) return;
                setAiGenerating(true);
                try {
                  const { data, error } = await supabase.functions.invoke('generate-comments', {
                    body: {
                      count: maxLines,
                      description: aiDescription || undefined,
                      language: aiLanguage,
                      emojis: aiEmojis === 'yes' ? true : aiEmojis === 'no' ? false : undefined,
                      emojiCount: aiEmojis === 'yes' ? 2 : undefined,
                    },
                  });
                  if (error) throw error;
                  if (data?.comments?.length) {
                    setCustomInput(data.comments.slice(0, maxLines).join('\n'));
                  }
                } catch (err: any) {
                  console.error('AI generate error:', err);
                } finally {
                  setAiGenerating(false);
                }
              };

              return (
                <div className="mb-4 border-b pb-4 space-y-2">
                  <label className="text-sm font-semibold">{customInputLabel}</label>
                  {/* AI Generation Panel */}
                  {aiGenerateEnabled && (
                    <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2.5">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        <span className="text-sm font-semibold text-foreground">{t('product.ai_generate', 'KI-Kommentare generieren')}</span>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs text-muted-foreground">{t('product.ai_describe_post', 'Beschreibe deinen Post')}</label>
                        <input
                          type="text"
                          value={aiDescription}
                          onChange={e => setAiDescription(e.target.value)}
                          placeholder={t('product.ai_describe_placeholder', 'z.B. Foto am Strand im Urlaub')}
                          className="w-full rounded-md border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                        />
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <div className="flex-1 min-w-[120px]">
                          <label className="mb-1 block text-xs text-muted-foreground">{t('product.ai_language', 'Sprache')}</label>
                          <select
                            value={aiLanguage}
                            onChange={e => setAiLanguage(e.target.value)}
                            className="w-full rounded-md border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                          >
                            <option value="Deutsch">Deutsch</option>
                            <option value="English">English</option>
                            <option value="Französisch">Français</option>
                            <option value="Italienisch">Italiano</option>
                            <option value="Spanisch">Español</option>
                            <option value="Portugiesisch">Português</option>
                          </select>
                        </div>
                        <div className="flex-1 min-w-[120px]">
                          <label className="mb-1 block text-xs text-muted-foreground">{t('product.ai_emojis', 'Emojis')}</label>
                          <select
                            value={aiEmojis}
                            onChange={e => setAiEmojis(e.target.value as any)}
                            className="w-full rounded-md border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                          >
                            <option value="yes">{t('product.ai_emojis_yes', 'Ja, mit Emojis')}</option>
                            <option value="few">{t('product.ai_emojis_few', 'Wenige Emojis')}</option>
                            <option value="no">{t('product.ai_emojis_no', 'Keine Emojis')}</option>
                          </select>
                        </div>
                      </div>
                      <button
                        onClick={handleAiGenerate}
                        disabled={aiGenerating}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90 disabled:opacity-50"
                      >
                        {aiGenerating ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t('product.ai_generating', 'Wird generiert...')}
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4" />
                            {t('product.ai_generate_btn', 'Kommentare generieren')}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {filledCount}/{maxLines} {t('product.entries_filled', 'ausgefüllt')}
                  </p>
                  <div className="overflow-hidden rounded-lg border bg-card">
                    <div className="flex">
                      <div
                        ref={customInputGutterRef}
                        className="max-h-[260px] shrink-0 overflow-hidden border-r bg-muted/40 py-2 text-right font-mono text-xs leading-[1.7rem] text-muted-foreground"
                        style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem', minWidth: '2.5rem' }}
                        aria-hidden="true"
                      >
                        {Array.from({ length: maxLines }, (_, i) => (
                          <div key={i}>{i + 1}.</div>
                        ))}
                      </div>
                      <textarea
                        ref={customInputTextareaRef}
                        value={customInput}
                        onChange={e => setNormalizedInput(e.target.value)}
                        onScroll={syncCustomInputScroll}
                        onPaste={e => {
                          e.preventDefault();
                          const textarea = e.currentTarget;
                          const pastedText = e.clipboardData.getData('text').replace(/\r\n/g, '\n');
                          const nextValue = textarea.value.slice(0, textarea.selectionStart) + pastedText + textarea.value.slice(textarea.selectionEnd);
                          setNormalizedInput(nextValue);
                        }}
                        rows={visibleLineCount}
                        placeholder={customInputPlaceholder || t('product.bulk_placeholder', 'Ein Kommentar pro Zeile')}
                        className="max-h-[260px] flex-1 resize-none overflow-y-auto overflow-x-auto bg-transparent py-2 px-3 text-sm outline-none leading-[1.7rem] whitespace-pre"
                        wrap="off"
                        spellCheck={false}
                      />
                    </div>
                  </div>
                </div>
              );
            })()}

            {sortedGifts.length > 0 && (() => {
              return (
                <div className="mb-4 flex items-center gap-4 border-b pb-4">
                  <label className="w-36 shrink-0 text-sm font-semibold">
                    {product.gift_label || t('product.free_gift', 'Geschenk 🎁')}
                  </label>
                  <div className="relative flex-1" data-dropdown>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === 'gift' ? null : 'gift')}
                      className="flex w-full items-center justify-between rounded-lg border bg-card px-4 py-2.5 text-sm transition-all hover:border-primary/40"
                    >
                      <span>{selectedGift ? (giftTranslations[selectedGift.id]?.label?.trim() || selectedGift.label) : t('product.no_thanks', 'Nein, danke')}</span>
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${openDropdown === 'gift' ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === 'gift' && (
                      <div className="absolute z-20 mt-1 w-full rounded-lg border bg-card shadow-lg">
                        <button
                          onClick={() => {
                            setSelectedGiftId(null);
                            setGiftTarget('');
                            setOpenDropdown(null);
                          }}
                          className={`flex w-full items-center px-4 py-2.5 text-left text-sm transition-all first:rounded-t-lg ${
                            !selectedGiftId
                              ? 'bg-primary font-semibold text-primary-foreground'
                              : 'text-foreground hover:bg-accent'
                          }`}
                        >
                          {t('product.no_thanks', 'Nein, danke')}
                        </button>
                        {sortedGifts.map((g: any) => {
                          const isActive = g.id === selectedGiftId;
                          return (
                            <button
                              key={g.id}
                              onClick={() => {
                                setSelectedGiftId(g.id);
                                setGiftTarget('');
                                setOpenDropdown(null);
                              }}
                              className={`flex w-full items-center px-4 py-2.5 text-left text-sm transition-all last:rounded-b-lg ${
                                isActive
                                  ? 'bg-primary font-semibold text-primary-foreground'
                                  : 'text-foreground hover:bg-accent'
                              }`}
                            >
                              {giftTranslations[g.id]?.label?.trim() || g.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Gift target input */}
            {selectedGift?.input_label && (
              <div
                ref={giftTargetFieldRef}
                className={`mb-4 flex items-center gap-4 border-b pb-4 rounded-lg transition-all ${
                  missingFields.has('giftTarget') ? 'ring-2 ring-destructive ring-offset-2 bg-destructive/5 px-2 py-2' : ''
                }`}
              >
                <label className="w-36 shrink-0 text-sm font-semibold">{giftTranslations[selectedGift.id]?.input_label?.trim() || selectedGift.input_label}</label>
                <input
                  type="text"
                  value={giftTarget}
                  onChange={e => { setGiftTarget(e.target.value); if (e.target.value.trim()) clearMissing('giftTarget'); }}
                  placeholder={giftTranslations[selectedGift.id]?.input_placeholder?.trim() || selectedGift.input_placeholder || ''}
                  className="w-full flex-1 rounded-lg border bg-card px-4 py-2.5 text-sm outline-none transition-all focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            )}

            {/* Confirmation */}
            <label
              ref={confirmFieldRef}
              className={`mb-5 flex items-start gap-3 cursor-pointer rounded-lg p-2 transition-all ${
                missingFields.has('confirm') ? 'ring-2 ring-destructive ring-offset-2 bg-destructive/5' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => { setConfirmed(e.target.checked); if (e.target.checked) clearMissing('confirm'); }}
                className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
              />
              <span className={`text-sm ${missingFields.has('confirm') ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                {t('product.confirm_public', 'Ich bestätige, dass mein Profil öffentlich ist.')} *
              </span>
            </label>

            {/* Price + Add */}
            <div className="mb-4 flex items-center justify-between">
              <div>
                <span className="font-mono text-2xl font-bold">
                  <DisplayPrice chf={variation ? variation.price : 0} variationId={variation?.id} />
                </span>
              </div>
            </div>

            {variation && variation.price > 0 && (
              <PointsEarnInfo amount={variation.price} className="mb-4" />
            )}

            {(product as any).is_available === false ? (
              <div className="w-full rounded-lg border border-amber-300 bg-amber-50 py-3.5 text-center text-sm font-semibold text-amber-700">
                {t('product.not_available', 'Dieser Service ist momentan nicht verfügbar.')}
              </div>
            ) : (
              (() => {
                const looksDisabled = !variation || !target.trim() || !confirmed || (!!selectedGift && !!selectedGift.input_label && !giftTarget.trim());
                return (
                  <motion.button
                    whileTap={{ scale: looksDisabled ? 1 : 0.98 }}
                    onClick={handleAddToCart}
                    aria-disabled={looksDisabled}
                    className={`group relative w-full overflow-hidden rounded-lg bg-cta py-4 text-base font-bold text-cta-foreground shadow-lg shadow-cta/30 transition-all hover:bg-cta-hover hover:shadow-xl hover:shadow-cta/40 ${
                      looksDisabled ? 'opacity-50 shadow-none cursor-pointer' : ''
                    }`}
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      <ShoppingCart className="h-5 w-5" />
                      {t('product.add_to_cart', 'In den Warenkorb')}
                    </span>
                    {!looksDisabled && (
                      <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                    )}
                  </motion.button>
                );
              })()
            )}

            {/* Trust line */}
            {(product as any).is_available !== false && (
              <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                <Lock className="h-3 w-3" />
                <span>{t('product.secure_checkout', 'Sichere Bezahlung · SSL-verschlüsselt')}</span>
              </div>
            )}


            {/* Payment icons */}
            <div className="mt-3">
              <PaymentIcons />
            </div>
          </div>

          {/* Right: Info */}
          <div>
            {/* Types overview */}
            {hasTypes && sortedTypes.some((tp: any) => tp.description || typeTranslations[tp.id]?.description) && (
              <div className="mb-8">
                <h2 className="mb-4 text-lg font-semibold">
                  {(() => {
                    const title = product.title.toLowerCase();
                    if (title.includes('follower')) return t('product.types_overview_follower', 'Follower-Typen im Überblick');
                    return t('product.types_overview', 'Typen im Überblick');
                  })()}
                </h2>
                <div className="grid gap-3">
                  {sortedTypes.map((tp: any) => {
                    const desc = typeTranslations[tp.id]?.description || tp.description;
                    if (!desc) return null;
                    const label = typeTranslations[tp.id]?.label || tp.label;
                    return (
                      <div key={tp.id} className="flex gap-3 rounded-lg border bg-card p-3.5">
                        {tp.emoji && (
                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent text-lg">
                            {tp.emoji}
                          </span>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground">{label}</p>
                          <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <hr className="mt-6" />
              </div>
            )}

            {/* Related products */}
            {relatedProducts.length > 0 && (
              <div className="mb-8">
                <h2 className="mb-3 text-lg font-semibold">{t('product.also_popular', 'Ebenfalls beliebt')}</h2>
                <div className="space-y-2">
                  {relatedProducts.map(rp => (
                    <RelatedProductLink key={rp.id} rp={rp} to={localePath(`/produkt/${rp.slug}`)} />
                  ))}
                </div>
              </div>
            )}

            {/* Trust badges */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 rounded-lg border p-4">
                <Truck className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-sm font-semibold">{t('product.fast_results', 'Schnelle Resultate')}</p>
                  <p className="text-xs text-muted-foreground">{t('product.within', 'Innerhalb von')} {product.delivery_time}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border p-4">
                <Shield className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-sm font-semibold">{t('product.secure_payment', 'Sichere Zahlung durch SSL Schutz')}</p>
                  <p className="text-xs text-muted-foreground">{t('product.secure_payment_desc', 'Sicher bezahlen durch SSL Verschlüsselung')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border p-4">
                <Tag className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-sm font-semibold">{t('product.best_prices', 'Günstigste Preise')}</p>
                  <p className="text-xs text-muted-foreground">{sf.storefront === 'at'
                    ? 'Die günstigsten Preise in ganz Österreich'
                    : t('product.best_prices_desc', 'Die günstigsten Preise in der ganzen Schweiz')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rich Description – full width */}
        {product.rich_description ? (
          <ProductDescription data={product.rich_description as any} productTitle={product.title} />
        ) : product.description && (
          <div className="mt-12">
            <h2 className="mb-3 text-lg font-semibold">{t('product.description', 'Beschreibung')}</h2>
            <p className="text-sm text-muted-foreground text-pretty">{product.description}</p>
          </div>
        )}

        {/* Reviews */}
        <ReviewSection productId={product.id} productTitle={product.title} />
      </div>
    </PageTransition>
  );
}

function RelatedProductLink({ rp, to }: { rp: { id: string; title: string; slug: string; image_url?: string | null }; to: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-accent"
    >
      <div className="flex h-10 w-10 items-center justify-center">
        {rp.image_url && !failed ? (
          <img
            src={transformImage(rp.image_url, { width: 40, height: 40, resize: 'contain' })}
            alt=""
            className="h-10 w-10 object-contain"
            loading="lazy"
            width={40}
            height={40}
            onError={() => setFailed(true)}
          />
        ) : (
          <Package className="h-6 w-6 text-muted-foreground/40" aria-hidden />
        )}
      </div>
      <span className="text-sm font-medium text-primary hover:underline">{rp.title}</span>
    </Link>
  );
}
